
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProviderAccess } from '@/hooks/useProviderAccess';
import { TimeSlotViewer } from '@/components/provider/TimeSlotViewer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ProviderHeader from '@/components/ProviderHeader';

const ProviderBookingDetailTest = () => {
  const { user } = useAuth();
  const { data: providerAccess } = useProviderAccess();

  console.log('=== PROVIDER BOOKING DETAIL TEST ===');
  console.log('User:', user?.email);
  console.log('Provider access:', providerAccess);
  
  // Test query to see what time slots are visible
  const { data: timeSlots, isLoading } = useQuery({
    queryKey: ['test-time-slots'],
    queryFn: async () => {
      console.log('Fetching time slots for testing...');
      
      const { data, error } = await supabase
        .from('time_slots')
        .select(`
          *,
          service:services(id, name, duration_minutes),
          room:rooms(id, name)
        `)
        .order('start_time', { ascending: true });

      console.log('Time slots query result:', { data, error });
      
      if (error) {
        console.error('Error fetching time slots:', error);
        throw error;
      }
      
      return data;
    },
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProviderHeader />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">Loading test page...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProviderHeader />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Time Slots RLS Policy Test</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold">Authentication Status:</h4>
                  <p>User: {user?.email || 'Not authenticated'}</p>
                  <p>Provider Access: {providerAccess ? 'Available' : 'Not available'}</p>
                  {providerAccess && (
                    <div className="mt-2 text-sm text-gray-600">
                      <p>Provider ID: {providerAccess.provider_id}</p>
                      <p>Role: {providerAccess.role}</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <h4 className="font-semibold">Time Slots Query Results:</h4>
                  <p>Total slots visible: {timeSlots?.length || 0}</p>
                  
                  {timeSlots && timeSlots.length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-medium mb-2">Slot Details:</h5>
                      <div className="space-y-2 text-sm">
                        {timeSlots.map((slot) => (
                          <div key={slot.id} className="p-2 bg-gray-100 rounded">
                            <div>ID: {slot.id}</div>
                            <div>Start: {new Date(slot.start_time).toLocaleString()}</div>
                            <div>Available: {slot.is_available ? 'Yes' : 'No'}</div>
                            <div>Bookings: {slot.current_bookings}/{slot.max_bookings}</div>
                            <div>Service: {slot.service?.name || 'None'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {providerAccess && (
            <Card>
              <CardHeader>
                <CardTitle>Provider-Specific Time Slots</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <h4 className="font-semibold mb-4">
                    Provider {providerAccess.provider_id} (Role: {providerAccess.role})
                  </h4>
                  <TimeSlotViewer providerId={providerAccess.provider_id} />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderBookingDetailTest;
